/**
 * Generic Share Code Page
 * Handles all types of share codes via unified /api/sharecode/{code} endpoint
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Container, Box, CircularProgress, Alert, Typography, Paper, TextField, Button, InputAdornment } from '@mui/material'
import { ErrorOutline as ErrorIcon, QrCode2, ArrowForward } from '@mui/icons-material'
import { socialApi } from '@api/endpoints/social'
import { rewardsApi } from '@api/endpoints/rewards'
import { useAuthStore } from '@store/authStore'
import { JoinGroupContent } from '../components/JoinGroupContent'
import { RegisterContent } from '../components/RegisterContent'
import type { ValidateRewardCodeResponse } from '@api/types/rewards'
import { RedeemPreviewDialog } from '@features/rewards/components/RedeemPreviewDialog'

export function ShareCodePage() {
  const { shareCode } = useParams<{ shareCode: string }>()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const [manualCode, setManualCode] = useState('')

  // Reward redemption state
  const [rewardValidation, setRewardValidation] = useState<ValidateRewardCodeResponse | null>(null)
  const [showRedeemDialog, setShowRedeemDialog] = useState(false)
  const [isRedeeming, setIsRedeeming] = useState(false)

  // Show manual entry form if no code provided
  if (!shareCode) {
    const handleSubmit = () => {
      if (manualCode.trim()) {
        navigate(`/code/${manualCode.trim()}`)
      }
    }

    const handleKeyPress = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSubmit()
      }
    }

    return (
      <Container maxWidth="sm">
        <Box sx={{ py: 8 }}>
          <Paper elevation={3} sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <QrCode2 sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
              <Typography variant="h4" gutterBottom>
                Enter Share Code
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Enter your invitation or referral code below
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Share Code"
                placeholder="e.g., ABC123XY"
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value.toUpperCase())}
                onKeyPress={handleKeyPress}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <QrCode2 />
                    </InputAdornment>
                  ),
                }}
                autoFocus
              />
            </Box>

            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handleSubmit}
              disabled={!manualCode.trim()}
              endIcon={<ArrowForward />}
            >
              Continue
            </Button>

            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Don't have a code?{' '}
                <Button
                  variant="text"
                  size="small"
                  onClick={() => navigate('/register')}
                >
                  Register here
                </Button>
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Container>
    )
  }

  // Use unified sharecode endpoint for all codes
  const { data: response, isLoading, error } = useQuery({
    queryKey: ['shareCode', shareCode],
    queryFn: () => socialApi.processShareCode(shareCode),
    retry: false,
  })

  // Handle navigation based on response action
  useEffect(() => {
    if (!response) return

    switch (response.action) {
      case 'AuthenticationRequired':
        // For reward codes, stay on page to show login prompt
        if (response.codeType === 'Reward') {
          // Will be handled in render
          return
        }
        // For user codes, pass the full code as referral code
        // For other codes, use the creator's referral code if available
        const referralCodeToPass = response.codeType === 'User' || shareCode.toUpperCase().startsWith('U')
          ? shareCode
          : response.creatorReferralCode
        // Redirect to login
        navigate('/login', {
          state: {
            returnUrl: `/code/${shareCode}`,
            message: response.message,
            referralCode: referralCodeToPass
          }
        })
        break

      case 'RewardCodeValid':
        // Show reward redeem dialog
        if (response.rewardInfo) {
          const validation: ValidateRewardCodeResponse = {
            valid: true,
            canRedeem: response.canRedeem ?? true,
            message: response.message || null,
            instanceId: response.rewardInfo.instanceId,
            title: response.rewardInfo.templateName,
            owner: null,
            ownerDisplayName: response.rewardInfo.ownerDisplayName || null,
            remainingUses: response.rewardInfo.remainingUses,
            isExpired: response.rewardInfo.isExpired ?? false,
            canOverrideExpiration: response.rewardInfo.canOverrideExpiration ?? false,
          }
          setRewardValidation(validation)
          setShowRedeemDialog(true)
        }
        break

      case 'GeocacheComplete':
        navigate(`/adventures/${response.adventureId}`, {
          state: {
            geocacheComplete: true,
            justCompleted: true,
            message: response.message || `${response.adventureName} completed!`
          }
        })
        break

      case 'RequirementFulfilled':
        navigate(`/adventures/${response.adventureId}`, {
          state: {
            requirementFulfilled: true,
            locationId: response.locationId,
            requirementId: response.requirementId,
            message: response.message || 'Requirement fulfilled!'
          }
        })
        break

      case 'DiscoveryOnly':
        if (response.redirectUrl) {
          navigate(response.redirectUrl, {
            state: { fromShareCode: true, message: response.message }
          })
        } else if (response.targetType === 'Adventure' && response.targetId) {
          navigate(`/adventures/${response.targetId}`, {
            state: { fromShareCode: true, message: response.message }
          })
        } else if (response.targetType === 'Group' && response.targetId) {
          navigate(`/groups/${response.targetId}`, {
            state: { fromShareCode: true, message: response.message }
          })
        } else if ((response.targetType === 'User' || response.targetType === 'Referral') && response.targetId) {
          if (isAuthenticated) {
            navigate(`/profile/${response.targetId}`, {
              state: { fromShareCode: true, message: response.message }
            })
          } else {
            navigate(`/register?code=${encodeURIComponent(shareCode)}`, {
              state: { fromShareCode: true, message: response.message }
            })
          }
        }
        break

      default:
        // For successful responses with redirect URLs
        if (response.success && response.redirectUrl) {
          navigate(response.redirectUrl, {
            state: { fromShareCode: true, message: response.message }
          })
        } else if (response.success && response.adventureId) {
          navigate(`/adventures/${response.adventureId}`, {
            state: { fromShareCode: true, message: response.message }
          })
        }
        break
    }
  }, [response, navigate, shareCode, isAuthenticated])

  // Handle reward redemption
  const handleRewardRedeem = async (overrideExpiration: boolean) => {
    if (!shareCode) return

    setIsRedeeming(true)
    try {
      const result = await rewardsApi.redeemReward({
        uniqueCode: shareCode,
        forceExpiredRedemption: overrideExpiration,
      })

      if (result.success) {
        setShowRedeemDialog(false)
        navigate('/rewards', {
          state: {
            redeemSuccess: true,
            message: result.message || 'Reward redeemed successfully!',
          },
        })
      } else {
        throw new Error(result.message || 'Failed to redeem reward')
      }
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { message?: string } }; message?: string }
      throw new Error(
        axiosError.response?.data?.message || axiosError.message || 'Failed to redeem reward'
      )
    } finally {
      setIsRedeeming(false)
    }
  }

  const handleRedeemDialogClose = () => {
    setShowRedeemDialog(false)
    setRewardValidation(null)
    navigate('/')
  }

  // Loading state
  if (isLoading) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 2 }}>
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary">
            Validating share code...
          </Typography>
        </Box>
      </Container>
    )
  }

  // Error state (network error)
  if (error) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ py: 8 }}>
          <Alert severity="error" icon={<ErrorIcon />}>
            <Typography variant="h6" gutterBottom>
              Unable to Load Share Code
            </Typography>
            <Typography variant="body2">
              {error instanceof Error ? error.message : 'Failed to validate share code. Please try again.'}
            </Typography>
          </Alert>
        </Box>
      </Container>
    )
  }

  // Handle various response states
  if (response) {
    // Authentication required for reward
    if (response.action === 'AuthenticationRequired' && response.codeType === 'Reward') {
      return (
        <Container maxWidth="sm">
          <Box sx={{ py: 8 }}>
            <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
              <QrCode2 sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Sign In Required
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                {response.message || 'Sign in to redeem this reward code.'}
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/login', {
                  state: { returnUrl: `/code/${shareCode}` }
                })}
              >
                Sign In
              </Button>
            </Paper>
          </Box>
        </Container>
      )
    }

    // Invalid code
    if (response.action === 'InvalidCode' || !response.success) {
      return (
        <Container maxWidth="sm">
          <Box sx={{ py: 8 }}>
            <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
              <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Invalid Share Code
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                {response.message || 'This share code is invalid, expired, or has reached its maximum uses.'}
              </Typography>
            </Paper>
          </Box>
        </Container>
      )
    }

    // Already completed
    if (response.action === 'AlreadyCompleted') {
      return (
        <Container maxWidth="sm">
          <Box sx={{ py: 8 }}>
            <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
              <QrCode2 sx={{ fontSize: 80, color: 'info.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Already Completed
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                {response.message || 'You have already completed this adventure.'}
              </Typography>
              {response.adventureId && (
                <Button
                  variant="contained"
                  onClick={() => navigate(`/adventures/${response.adventureId}`)}
                >
                  View Adventure
                </Button>
              )}
            </Paper>
          </Box>
        </Container>
      )
    }

    // Wrong location
    if (response.action === 'WrongLocation') {
      return (
        <Container maxWidth="sm">
          <Box sx={{ py: 8 }}>
            <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
              <ErrorIcon sx={{ fontSize: 80, color: 'warning.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Wrong Location
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                {response.message || 'This code is valid but not for your current location in the adventure.'}
              </Typography>
              {response.adventureId && (
                <Button
                  variant="contained"
                  onClick={() => navigate(`/adventures/${response.adventureId}`)}
                >
                  View Adventure
                </Button>
              )}
            </Paper>
          </Box>
        </Container>
      )
    }

    // Reward code valid - show dialog
    if (response.action === 'RewardCodeValid') {
      return (
        <Container maxWidth="sm">
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <CircularProgress size={60} />
            <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
              Loading reward...
            </Typography>
          </Box>
          <RedeemPreviewDialog
            open={showRedeemDialog}
            onClose={handleRedeemDialogClose}
            validation={rewardValidation}
            onRedeem={handleRewardRedeem}
            isRedeeming={isRedeeming}
          />
        </Container>
      )
    }

    // Requires additional criteria
    if (response.requiresAdditionalCriteria) {
      return (
        <Container maxWidth="sm">
          <Box sx={{ py: 8 }}>
            <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
              <ErrorIcon sx={{ fontSize: 80, color: 'warning.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Requirements Not Met
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                {response.message || 'You do not meet the required criteria to access this content.'}
              </Typography>
              {response.redirectUrl && (
                <Button
                  variant="contained"
                  onClick={() => navigate(response.redirectUrl!)}
                >
                  View Details
                </Button>
              )}
            </Paper>
          </Box>
        </Container>
      )
    }
  }

  // Default loading/processing state while useEffect handles navigation
  return (
    <Container maxWidth="sm">
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 2 }}>
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Processing...
        </Typography>
      </Box>
      <RedeemPreviewDialog
        open={showRedeemDialog}
        onClose={handleRedeemDialogClose}
        validation={rewardValidation}
        onRedeem={handleRewardRedeem}
        isRedeeming={isRedeeming}
      />
    </Container>
  )
}
